/* eslint-disable no-unused-vars */
import { Context, Data, Effect, Layer } from "effect";
import { type ShippingPolicySchema } from "../policies/ShippingPolicy";
import {
  calculateGrandTotal,
  validateGrandTotalCalculation,
} from "../value-objects/InvoiceFinancials";
import { type Money } from "../value-objects/Money";

export class FinancialValidationError extends Data.TaggedError(
  "FinancialValidationError"
)<{
  readonly violations: string[];
  readonly message: string;
}> {}

type BaseFinancials = {
  subtotal: Money;
  taxes: Money;
  shippingCost: Money;
  discount: Money;
};

/**
 * Domain Service for financial calculations and business rule validation.
 *
 * This service encapsulates complex business rules that don't naturally belong
 * to a single entity or value object. It coordinates between multiple domain objects
 * and provides higher-level financial operations.
 *
 * In DDD, this service should be called by:
 * - Aggregate Roots (Invoice, Order)
 * - Application Services (when orchestrating domain operations)
 * - Other Domain Services (for complex domain operations)
 */
export class FinancialCalculationService extends Context.Tag(
  "FinancialCalculationService"
)<
  FinancialCalculationService,
  {
    /**
     * Calculates the grand total according to business rules.
     * Delegates to pure domain logic in value objects.
     */
    calculateGrandTotal(
      subtotal: Money,
      taxes: Money,
      shippingCost: Money,
      discount: Money
    ): Money;

    /**
     * Validates that a discount amount doesn't exceed the maximum allowable discount.
     * Business rule: discount cannot exceed subtotal + taxes + shippingCost
     */
    validateDiscountLimit(financials: {
      subtotal: Money;
      taxes: Money;
      shippingCost: Money;
      discount: Money;
    }): boolean;

    /**
     * Validates that the grand total calculation is correct according to business rules.
     * Delegates to pure domain logic in value objects.
     */
    validateGrandTotalCalculation(financials: {
      subtotal: Money;
      taxes: Money;
      shippingCost: Money;
      discount: Money;
      grandTotal: Money;
    }): boolean;

    /**
     * Complex business rule: Validates financial consistency across an entire invoice.
     * This is a higher-level operation that coordinates multiple validations.
     */
    validateInvoiceFinancialConsistency(
      financials: {
        subtotal: Money;
        taxes: Money;
        shippingCost: Money;
        discount: Money;
        grandTotal: Money;
      },
      shippingPolicy: ShippingPolicySchema
    ): Effect.Effect<{ isValid: boolean }, FinancialValidationError>;
  }
>() {}

/**
 * Default implementation of the FinancialCalculationService
 */
export const FinancialCalculationServiceLive = Layer.succeed(
  FinancialCalculationService,
  {
    calculateGrandTotal: (
      subtotal: Money,
      taxes: Money,
      shippingCost: Money,
      discount: Money
    ): Money => calculateGrandTotal(subtotal, taxes, shippingCost, discount),

    validateDiscountLimit: (financials: {
      subtotal: Money;
      taxes: Money;
      shippingCost: Money;
      discount: Money;
    }): boolean => validateDiscountLimit(financials),

    validateGrandTotalCalculation: (financials: {
      subtotal: Money;
      taxes: Money;
      shippingCost: Money;
      discount: Money;
      grandTotal: Money;
    }): boolean => validateGrandTotalCalculation(financials),

    validateInvoiceFinancialConsistency: (
      financials: {
        subtotal: Money;
        taxes: Money;
        shippingCost: Money;
        discount: Money;
        grandTotal: Money;
      },
      shippingPolicy: ShippingPolicySchema
    ) => {
      return Effect.gen(function* () {
        const isEligible = yield* shippingPolicy.isFreeShippingEligible(
          financials
        );
        if (isEligible && financials.shippingCost.amount !== 0) {
          return yield* Effect.fail(
            new FinancialValidationError({
              violations: ["FreeShippingPolicy"],
              message: `Free shipping applies, but shipping costs are positive: ${financials.shippingCost.amount}`,
            })
          );
        }
        const result = validateInvoiceFinancialConsistency(financials);
        return { isValid: result.isValid };
      });
    },
  }
);

/**
 * Convenience functions for direct access to the service methods
 * These can be used by aggregates and other domain services
 */

/**
 * Business rule validation for discount limits
 */
export const validateDiscountLimit = (financials: BaseFinancials): boolean => {
  const maxDiscount = financials.subtotal.addMultiple(
    financials.taxes,
    financials.shippingCost
  );
  return financials.discount.isLess(maxDiscount, "orEqual");
};

/**
 * Complex business rule that coordinates multiple validations.
 * This is the kind of logic that belongs in a domain service.
 */
export const validateInvoiceFinancialConsistency = (financials: {
  subtotal: Money;
  taxes: Money;
  shippingCost: Money;
  discount: Money;
  grandTotal: Money;
}): {
  isValid: boolean;
  violations: string[];
} => {
  const violations: string[] = [];

  // Check discount limit
  if (!validateDiscountLimit(financials)) {
    violations.push("Discount exceeds maximum allowable amount");
  }

  // Check grand total calculation
  if (!validateGrandTotalCalculation(financials)) {
    violations.push("Grand total calculation is incorrect");
  }

  // Additional business rules can be added here
  // For example: tax calculation validation, shipping cost validation, etc.

  return {
    isValid: violations.length === 0,
    violations,
  };
};
